import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [loading, setLoading] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({ score: 0, text: '', color: '' });

  // Verify token on page load
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const response = await axios.post(`${API}/auth/verify-reset-token`, { token });
        setTokenValid(true);
        setEmail(response.data.email);
      } catch (error) {
        setTokenValid(false);
        toast.error(error.response?.data?.detail || 'Invalid or expired reset link');
      } finally {
        setLoading(false);
      }
    };

    verifyToken();
  }, [token]);

  // Password strength checker
  const checkPasswordStrength = (pwd) => {
    let score = 0;
    if (pwd.length >= 6) score++;
    if (pwd.length >= 8) score++;
    if (/[a-z]/.test(pwd) && /[A-Z]/.test(pwd)) score++;
    if (/\d/.test(pwd)) score++;
    if (/[^a-zA-Z0-9]/.test(pwd)) score++;

    const levels = [
      { text: 'Very Weak', color: '#dc3545' },
      { text: 'Weak', color: '#fd7e14' },
      { text: 'Fair', color: '#ffc107' },
      { text: 'Good', color: '#20c997' },
      { text: 'Strong', color: '#28a745' }
    ];

    setPasswordStrength({
      score,
      text: levels[Math.min(score, 4)].text,
      color: levels[Math.min(score, 4)].color
    });
  };

  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    checkPasswordStrength(newPassword);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters long');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setSubmitting(true);

    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: password
      });
      setSuccess(true);
      toast.success('Password reset successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="auth-container" data-testid="reset-password-page">
        <div className="auth-card">
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>⏳</div>
            <p>Verifying reset link...</p>
          </div>
        </div>
      </div>
    );
  }

  // No token provided
  if (!token) {
    return (
      <div className="auth-container" data-testid="reset-password-page">
        <div className="auth-card">
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>❌</div>
            <h2 style={{ color: '#dc3545', marginBottom: '1rem' }}>Invalid Link</h2>
            <p style={{ color: '#6b8899', marginBottom: '1.5rem' }}>
              No reset token was provided. Please request a new password reset link.
            </p>
            <Link to="/forgot-password" className="submit-button" style={{ display: 'inline-block', textDecoration: 'none' }}>
              Request New Link
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Invalid/expired token
  if (!tokenValid) {
    return (
      <div className="auth-container" data-testid="reset-password-page">
        <div className="auth-card">
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>⏰</div>
            <h2 style={{ color: '#dc3545', marginBottom: '1rem' }}>Link Expired</h2>
            <p style={{ color: '#6b8899', marginBottom: '1.5rem' }}>
              This password reset link has expired or has already been used. 
              Please request a new one.
            </p>
            <Link to="/forgot-password" className="submit-button" style={{ display: 'inline-block', textDecoration: 'none' }}>
              Request New Link
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="auth-container" data-testid="reset-password-page">
        <div className="auth-card">
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>✅</div>
            <h2 style={{ color: '#28a745', marginBottom: '1rem' }}>Password Reset!</h2>
            <p style={{ color: '#6b8899', marginBottom: '1.5rem' }}>
              Your password has been successfully reset. You can now login with your new password.
            </p>
            <button 
              onClick={() => navigate('/auth')}
              className="submit-button"
            >
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Reset form
  return (
    <div className="auth-container" data-testid="reset-password-page">
      <div className="auth-card">
        <div className="auth-header">
          <img 
            src="/careguard-logo.png" 
            alt="CareGuard" 
            style={{ height: '60px', marginBottom: '1rem', borderRadius: '8px' }}
          />
          <h1>Reset Password</h1>
          <p>Create a new password for <strong>{email}</strong></p>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label htmlFor="password">New Password</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={handlePasswordChange}
              placeholder="Enter new password"
              required
              minLength={6}
              data-testid="password-input"
            />
            {password && (
              <div style={{ marginTop: '0.5rem' }}>
                <div style={{ 
                  height: '4px', 
                  background: '#e9ecef', 
                  borderRadius: '2px',
                  overflow: 'hidden'
                }}>
                  <div style={{ 
                    height: '100%', 
                    width: `${(passwordStrength.score / 5) * 100}%`,
                    background: passwordStrength.color,
                    transition: 'all 0.3s ease'
                  }} />
                </div>
                <p style={{ 
                  margin: '0.25rem 0 0', 
                  fontSize: '0.85rem', 
                  color: passwordStrength.color 
                }}>
                  Password strength: {passwordStrength.text}
                </p>
              </div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm New Password</label>
            <input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
              required
              minLength={6}
              data-testid="confirm-password-input"
            />
            {confirmPassword && password !== confirmPassword && (
              <p style={{ margin: '0.25rem 0 0', fontSize: '0.85rem', color: '#dc3545' }}>
                Passwords do not match
              </p>
            )}
            {confirmPassword && password === confirmPassword && password.length >= 6 && (
              <p style={{ margin: '0.25rem 0 0', fontSize: '0.85rem', color: '#28a745' }}>
                ✓ Passwords match
              </p>
            )}
          </div>

          {/* Password Requirements */}
          <div style={{ 
            background: '#f8f9fa', 
            padding: '1rem', 
            borderRadius: '8px',
            marginBottom: '1rem'
          }}>
            <p style={{ margin: '0 0 0.5rem', fontWeight: '600', color: '#2c5364' }}>
              Password Tips:
            </p>
            <ul style={{ margin: 0, paddingLeft: '1.25rem', color: '#6b8899', fontSize: '0.9rem' }}>
              <li style={{ color: password.length >= 6 ? '#28a745' : '#6b8899' }}>
                At least 6 characters
              </li>
              <li style={{ color: /[A-Z]/.test(password) && /[a-z]/.test(password) ? '#28a745' : '#6b8899' }}>
                Mix of uppercase and lowercase
              </li>
              <li style={{ color: /\d/.test(password) ? '#28a745' : '#6b8899' }}>
                Include a number
              </li>
              <li style={{ color: /[^a-zA-Z0-9]/.test(password) ? '#28a745' : '#6b8899' }}>
                Include a special character (!@#$...)
              </li>
            </ul>
          </div>

          <button 
            type="submit" 
            className="submit-button"
            disabled={submitting || password.length < 6 || password !== confirmPassword}
            data-testid="submit-button"
          >
            {submitting ? 'Resetting...' : 'Reset Password'}
          </button>

          <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
            <Link to="/auth" style={{ color: '#2c5364', textDecoration: 'none' }}>
              ← Back to Login
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ResetPassword;
